import { useEffect, useState } from 'react';
import {
  DEV_TICKET_CATEGORIES,
  DEV_TICKET_CATEGORY_LABELS,
  sanitizeReportUrl,
  shortUserAgent,
  type DevTicketCategory,
} from '@bala/core';
import { Button, Field, Input, SegmentedControl, Textarea } from './primitives';
import { Modal, useToast } from './overlays';

/**
 * Signalement d'un bug ou dépôt d'une idée, identique dans les deux
 * applications.
 *
 * Le composant ne connaît aucun client HTTP : il reçoit `onSubmit` et se
 * contente de constituer le message. C'est le même partage que
 * `PushDeviceCard` — les deux applications ont chacune leur client, et
 * l'importer ici aurait lié ce paquet d'interface à l'une d'elles.
 *
 * Ce qui part est montré avant l'envoi. Une capture silencieuse de l'URL
 * mettrait la personne devant le fait accompli, alors qu'elle est seule à
 * savoir si la page où elle se trouve la regarde.
 */
export interface ReportSubmission {
  category: DevTicketCategory;
  title: string;
  description: string;
  currentUrl: string;
}

export function ReportDialog({
  open,
  onClose,
  onSubmit,
  defaultCategory = 'bug',
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: (report: ReportSubmission) => Promise<void>;
  defaultCategory?: DevTicketCategory;
}) {
  const toast = useToast();
  const [category, setCategory] = useState<DevTicketCategory>(defaultCategory);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [sending, setSending] = useState(false);

  // Relevés à l'ouverture, et pas au montage : la personne a pu naviguer
  // depuis, et c'est la page d'où elle signale qui compte.
  const [contexte, setContexte] = useState({ url: '', appareil: '' });

  useEffect(() => {
    if (!open) return;
    setCategory(defaultCategory);
    setTitle('');
    setDescription('');
    setContexte({
      url: sanitizeReportUrl(typeof window === 'undefined' ? '' : window.location.href),
      appareil: shortUserAgent(typeof navigator === 'undefined' ? '' : navigator.userAgent),
    });
  }, [open, defaultCategory]);

  const envoyer = async () => {
    if (!title.trim() || !description.trim()) {
      toast.error('Un titre et une description sont nécessaires.');
      return;
    }
    setSending(true);
    try {
      await onSubmit({
        category,
        title: title.trim(),
        description: description.trim(),
        currentUrl: contexte.url,
      });
      toast.success(
        category === 'bug'
          ? 'Merci, le bug est transmis à l’équipe.'
          : 'Merci, l’idée est transmise à l’équipe.',
      );
      onClose();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Envoi impossible.');
    } finally {
      setSending(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Signaler un problème"
      subtitle="Un bug à corriger ou une idée à proposer"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Annuler
          </Button>
          <Button variant="primary" loading={sending} onClick={() => void envoyer()}>
            Envoyer
          </Button>
        </>
      }
    >
      <Field label="De quoi s’agit-il ?">
        <SegmentedControl
          value={category}
          onChange={setCategory}
          options={DEV_TICKET_CATEGORIES.map((value) => ({
            value,
            label: DEV_TICKET_CATEGORY_LABELS[value],
          }))}
          block
        />
      </Field>

      <Field label="En une phrase" required>
        <Input
          value={title}
          onChange={(event) => setTitle(event.target.value)}
          maxLength={120}
          placeholder={
            category === 'bug' ? 'Le bouton Envoyer ne répond pas' : 'Pouvoir filtrer par date'
          }
        />
      </Field>

      <Field
        label="Détails"
        required
        hint={
          category === 'bug'
            ? 'Ce que vous faisiez, ce que vous attendiez, ce qui s’est passé.'
            : 'À quoi cela servirait, et dans quelle situation.'
        }
      >
        <Textarea
          value={description}
          onChange={(event) => setDescription(event.target.value)}
          maxLength={4_000}
          rows={5}
        />
      </Field>

      <div
        style={{
          padding: 'var(--s-3)',
          background: 'var(--surface-2)',
          borderRadius: 'var(--r-md)',
          font: 'var(--t-caption)',
          color: 'var(--text-secondary)',
        }}
      >
        <p style={{ fontWeight: 600, marginBottom: 4 }}>Joint automatiquement</p>
        <p style={{ wordBreak: 'break-all' }}>Page : {contexte.url || '—'}</p>
        <p>Appareil : {contexte.appareil}</p>
      </div>
    </Modal>
  );
}
